/**
 * Sign-in and activation.
 *
 * Two surfaces, one component: the backend has separate member and admin session
 * kinds and will not let one stand in for the other, so the form states which
 * one it is signing into rather than pretending there is a single login.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError } from "../../lib/api.ts";
import { useSession } from "../../lib/session.tsx";
import { ThemeToggle } from "../../components/ThemeScript.tsx";
import { DesignatorTag, Field, Plate, SectionTitle, TextInput } from "../../components/kit.tsx";

/**
 * Chinese copy for a sign-in failure.
 *
 * The server speaks English deliberately: the contract is `{code, message,
 * details}` and `code` is the part a client should branch on, so the message is
 * free to change. The person reading it on screen is a club member, though, and
 * a form that rejects them in another language reads as a malfunction — which
 * is exactly how "no feedback" was reported.
 *
 * `UNAUTHENTICATED` is one server-side code for three different causes (unknown
 * account, wrong password, disabled account). The API refuses to distinguish
 * them so a stranger cannot enumerate accounts, so this string must refuse to
 * speculate too — claiming "password wrong" when the account was disabled would
 * be a lie, and claiming "no such account" would be a leak.
 */
function loginErrorMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) return "登录失败，请检查网络后重试。";
  if (caught.details.mustActivate === true) {
    return "这个账号还没有设置过密码，需要先用激活码激活才能登录。";
  }
  if (caught.isUnauthenticated) return "账号或密码不正确。";
  return caught.message;
}

/** True when the failure is "you have not activated yet", worth a link. */
function needsActivation(caught: unknown): boolean {
  return caught instanceof ApiError && caught.details.mustActivate === true;
}

export function SignIn({ surface = "member" }: { surface?: "member" | "admin" }) {
  const navigate = useNavigate();
  const { signInMember, signInAdmin } = useSession();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [needActivation, setNeedActivation] = useState(false);
  const [busy, setBusy] = useState(false);

  const isAdmin = surface === "admin";

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNeedActivation(false);
    try {
      if (isAdmin) {
        await signInAdmin(username, password);
        navigate("/admin");
      } else {
        await signInMember(username, password);
        navigate("/me");
      }
    } catch (caught) {
      setError(loginErrorMessage(caught));
      setNeedActivation(needsActivation(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main id="main" className="flex min-h-screen flex-col">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-8">
          <Link to="/" className="flex items-baseline gap-3">
            <span aria-hidden className="block h-3.5 w-3.5 border border-copper" />
            <span className="silk text-[15px]">科创中心</span>
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center px-6 py-16">
        <SectionTitle>{isAdmin ? "ADMIN / 管理入口" : "MEMBER / 成员入口"}</SectionTitle>
        <h1 className="silk mt-6 text-[clamp(1.75rem,4vw,2.5rem)]">
          {isAdmin ? "管理系统登录" : "成员中心登录"}
        </h1>
        <p className="mt-4 text-[13px] leading-relaxed text-silk-dim">
          {isAdmin
            ? "管理入口需要账号持有后台角色。没有角色的账号可以登录，但所有管理页面都会拒绝它。"
            : "用管理员发给你的账号登录。首次登录需要用激活码设置密码。"}
        </p>

        <form onSubmit={handleSubmit} className="mt-10 flex flex-col gap-5">
          <Plate className="flex flex-col gap-5 p-6">
            <Field label="账号" required htmlFor="username">
              <TextInput
                id="username"
                name="username"
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="你的账号"
              />
            </Field>
            <Field label="密码" required htmlFor="password">
              <TextInput
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
              />
            </Field>

            {error ? (
              <div role="alert" className="border border-state-danger bg-ground-sunken px-4 py-3 text-[13px] text-silk">
                <p>{error}</p>
                {needActivation ? (
                  <Link to="/activate" className="mt-2 inline-block underline decoration-copper underline-offset-4">
                    用激活码去设置密码 →
                  </Link>
                ) : null}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={busy || !username || !password}
              className="silk-label border border-signal bg-signal px-6 py-3.5 text-[13px] text-silk-inverse transition-colors hover:bg-signal/90 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "验证中" : "登录"}
            </button>
          </Plate>
        </form>

        <div className="mt-8 flex flex-col gap-3 text-[12px] text-silk-dim">
          <DesignatorTag code="J3" label="首次登录" />
          <p>
            收到激活码后，
            <Link to="/activate" className="mx-1 text-copper hover:underline">
              在这里设置密码
            </Link>
            。
          </p>
          <p>
            {isAdmin ? (
              <Link to="/sign-in" className="text-copper hover:underline">
                我是普通成员，去成员入口
              </Link>
            ) : (
              <Link to="/sign-in/admin" className="text-copper hover:underline">
                我是管理员，去管理入口
              </Link>
            )}
          </p>
        </div>
      </div>
    </main>
  );
}

/**
 * Activation.
 *
 * Redeems the one-time code the operator handed over. Codes are never emailed,
 * deliberately: a credential that travels by email ends up in an inbox.
 */
export function Activate() {
  const [code, setCode] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError("两次输入的密码不一致。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(
        "/api/activate",
        // `activationCode`, not `code`: this was the field mismatch that made
        // activation fail from the UI while the endpoint itself worked.
        { activationCode: code, username, newPassword: password },
        { skipCsrf: true }
      );
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "激活失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main id="main" className="flex min-h-screen flex-col">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-8">
          <Link to="/" className="flex items-baseline gap-3">
            <span aria-hidden className="block h-3.5 w-3.5 border border-copper" />
            <span className="silk text-[15px]">科创中心</span>
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center px-6 py-16">
        <SectionTitle>ACTIVATION / 激活</SectionTitle>
        <h1 className="silk mt-6 text-[clamp(1.75rem,4vw,2.5rem)]">设置密码</h1>

        {done ? (
          <Plate className="mt-10 p-8">
            <p className="text-[14px] text-silk">密码已设置，激活码已作废。</p>
            <p className="mt-3 text-[13px] text-silk-dim">现在可以用新密码登录。</p>
            <button
              type="button"
              onClick={() => navigate("/sign-in")}
              className="silk-label mt-6 border border-line-strong px-5 py-2.5 text-[12px] text-silk transition-colors hover:bg-silk hover:text-silk-inverse"
            >
              去登录
            </button>
          </Plate>
        ) : (
          <form onSubmit={handleSubmit} className="mt-10 flex flex-col gap-5">
            <Plate className="flex flex-col gap-5 p-6">
              <Field label="激活码" required hint="管理员当面给你的 10 位码" htmlFor="code">
                <TextInput
                  id="code"
                  value={code}
                  onChange={(event) => setCode(event.target.value.toUpperCase())}
                  placeholder="ABCD2345EF"
                  className="data tracking-[0.2em]"
                  maxLength={12}
                />
              </Field>
              <Field label="账号" required htmlFor="username">
                <TextInput
                  id="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                />
              </Field>
              <Field label="新密码" required htmlFor="password">
                <TextInput
                  id="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                />
              </Field>
              <Field label="再输入一次" required htmlFor="confirmation">
                <TextInput
                  id="confirmation"
                  type="password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  autoComplete="new-password"
                />
              </Field>

              {error ? (
                <p role="alert" className="border border-state-danger bg-ground-sunken px-4 py-3 text-[13px] text-silk">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={busy || !code || !username || !password}
                className="silk-label border border-signal bg-signal px-6 py-3.5 text-[13px] text-silk-inverse transition-colors hover:bg-signal/90 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? "激活中" : "激活并设置密码"}
              </button>
            </Plate>
          </form>
        )}
      </div>
    </main>
  );
}
